/**
 * The caller's ONDO account — principal, memberships, workspaces — as one
 * state the whole app reads through `AccountContext`.
 *
 * Keyed on the auth user id rather than on the session, so a token refresh
 * does not refetch and a response for a previous account can never be shown
 * against a new one. `unprovisioned` is a real state and not an error: the
 * person is signed in, and no record exists for them yet.
 *
 * `replace` is not a refetch. Every write that changes the account answers
 * with the whole `MeResponse` precisely so it can be handed straight back
 * here, with no window in which two screens disagree.
 */

import type { MeResponse } from "@ondo/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, fetchMe } from "./api.ts";

export type MeState =
  | { status: "loading" }
  | { status: "ready"; me: MeResponse }
  /** Authenticated, but no ONDO principal exists for this account yet. */
  | { status: "unprovisioned" }
  /** `unreachable` distinguishes "the API is down" from "the API said no". */
  | { status: "error"; message: string; unreachable: boolean };

/** Every state except `loading`, which is derived rather than stored. */
type Loaded = Exclude<MeState, { status: "loading" }>;

/**
 * Loads the caller's principal and memberships.
 *
 * Keyed on the user id so it refetches when the account changes, but not on
 * every token refresh — the response does not depend on the token's age.
 *
 * The result is stored alongside the id it belongs to, and `loading` is
 * derived from the two disagreeing. That avoids setting state synchronously
 * inside the effect (which cascades an extra render), and it means a response
 * for a previous account can never be displayed against a new one.
 */
export function useMe(userId: string | undefined): {
  state: MeState;
  /**
   * Replaces the loaded account with one the server just returned.
   *
   * `PUT /v1/me/profile` answers with the whole `MeResponse` rather than the
   * three fields it wrote, precisely so this can exist: a display name reaches
   * the sidebar's identity card and the workspace header, and without a way to
   * hand the new one back, saving your name would leave two screens showing the
   * old one until a reload.
   *
   * Not a refetch. The response IS the read — re-requesting it would be a second
   * round trip to learn what the first one already said, with a window in
   * between where the two disagree.
   */
  replace: (me: MeResponse) => void;
} {
  const [entry, setEntry] = useState<{ userId: string; state: Loaded } | null>(null);

  useEffect(() => {
    if (!userId) return;

    let active = true;

    fetchMe()
      .then((me) => {
        if (active) setEntry({ userId, state: { status: "ready", me } });
      })
      .catch((error: unknown) => {
        if (!active) return;

        const state: Loaded =
          error instanceof ApiError && error.status === 404
            ? { status: "unprovisioned" }
            : {
                status: "error",
                message:
                  error instanceof Error
                    ? error.message
                    : "Could not load your account.",
                unreachable: error instanceof ApiError && error.isUnreachable,
              };

        setEntry({ userId, state });
      });

    return () => {
      active = false;
    };
  }, [userId]);

  // Keyed on the id it belongs to for the same reason the fetch is: a response
  // for a previous account must not be written over a new one.
  const replace = useCallback(
    (me: MeResponse) => {
      if (!userId) return;
      setEntry({ userId, state: { status: "ready", me } });
    },
    [userId],
  );

  const state: MeState =
    !userId || entry?.userId !== userId ? { status: "loading" } : entry.state;

  return { state, replace };
}
